import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

// A list's filters and its page, kept in the address bar.
//
// They used to be component state, so a refresh — or opening the row you were
// looking at and pressing Back — dropped the filter and put you on page 1 of
// the whole register. In the URL they survive both, and a filtered view is a
// link someone can send to a colleague.
//
// `emptyFilters` names the fields and must be a stable object (a module-level
// constant, not a literal in the render body): the filters object is memoised
// on it, and the fetch effect keys off that identity.
//
// The URL carries the page 1-based, the way the API counts, while MUI's
// TablePagination counts from 0 — the conversion lives here so no caller has to
// remember it. Defaults (page 1, the default page size, a blank filter) are
// left out of the URL entirely rather than written as `?page=1&locationId=`.
export default function useUrlFilters(emptyFilters, { defaultRowsPerPage = 10 } = {}) {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo(() => {
    const current = {};
    Object.keys(emptyFilters).forEach((key) => {
      current[key] = searchParams.get(key) ?? emptyFilters[key];
    });
    return current;
  }, [searchParams, emptyFilters]);

  const hasFilters = useMemo(
    () => Object.values(filters).some((value) => value !== '' && value !== null && value !== undefined),
    [filters]
  );

  const pageNumber = parseInt(searchParams.get('page'), 10);
  const page = Number.isFinite(pageNumber) && pageNumber > 0 ? pageNumber - 1 : 0;

  const size = parseInt(searchParams.get('page_size'), 10);
  const rowsPerPage = Number.isFinite(size) && size > 0 ? size : defaultRowsPerPage;

  const updateParams = useCallback(
    (changes) => {
      const next = new URLSearchParams(searchParams);
      Object.entries(changes).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') {
          next.delete(key);
        } else {
          next.set(key, value);
        }
      });
      setSearchParams(next);
    },
    [searchParams, setSearchParams]
  );

  const setPage = useCallback((nextPage) => updateParams({ page: nextPage > 0 ? nextPage + 1 : '' }), [updateParams]);

  const setRowsPerPage = useCallback(
    (nextSize) =>
      // Back to the first page: page 4 of 40 rows may not exist at 100 a page.
      updateParams({ page_size: nextSize === defaultRowsPerPage ? '' : nextSize, page: '' }),
    [updateParams, defaultRowsPerPage]
  );

  const applyFilters = useCallback(
    (values) =>
      // A narrower result set has fewer pages, so start at the first one.
      updateParams({ ...emptyFilters, ...values, page: '' }),
    [updateParams, emptyFilters]
  );

  const clearFilters = useCallback(() => applyFilters(emptyFilters), [applyFilters, emptyFilters]);

  return { filters, hasFilters, page, rowsPerPage, setPage, setRowsPerPage, applyFilters, clearFilters };
}
